import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Map } from 'immutable';
import { map, Observable } from 'rxjs';

import {
	DEFAULT_PAGE_SIZE,
	DxFilterField,
	DxOperations,
	DxSortField,
} from '../../core/dx-types';
import { DxGridUtil } from '../../core/utils/dx-grid.util';
import { OperatorUtil } from '../../core/utils/operator-mapping.util';
import { ExpositionConsumerMappingEntity } from '../../expositions/domain/exposition-consumer-mapping.entity';
import { DcBaseRequester, IQueryParams } from './dc-base.requester';
import {
	FieldsMapping,
	IConsumersToMap,
	IConsumerToMap,
	IMarketplaceGroup,
	IMarketPlaceMembers,
	IMarketplaceUser,
	IMembersMapping,
} from './models/marketplace-members.model';

type WithDetails = 'MICRO' | 'ATTRIBUTES' | 'MINIMAL';

@Injectable({
	providedIn: 'root',
})
export class DcMarketPlaceRequester extends DcBaseRequester<IMarketPlaceMembers> {
	protected resource: string;
	protected elementsListUrl;
	protected singleElementUrl;

	private readonly fieldsMapping: { [k: string]: FieldsMapping } = {
		login: FieldsMapping.LOGIN,
		firstName: FieldsMapping.FIRSTNAME,
		lastName: FieldsMapping.LASTNAME,
		mail: FieldsMapping.MAIL,
		name: FieldsMapping.NAME,
		description: FieldsMapping.DESCRIPTION,
		updateDate: FieldsMapping.UPDATED_AT,
		creationDate: FieldsMapping.CREATED_AT,
	};
	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('DC_MARKETPLACE_MEMBERS');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getMarketPlaceMembers(
		useCache = true
	): Observable<IMarketPlaceMembers> {
		const url = this.urlService.generateUrl(this.resource, {}, {});
		const cachingPolicy = useCache
			? CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: true,
					policy: CachePolicy.NetworkAndCache,
			  })
			: CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
			  });

		return this.get<IMarketPlaceMembers>(url, cachingPolicy);
	}

	public getMarketPlaceMembersOnProjectImport(): Observable<IMarketPlaceMembers> {
		let url = this.generateResourceUrl(
			'PROJECTS_IMPORT_DC_MARKETPLACE_MEMBERS'
		);
		url = this.urlService.generateUrl(url, {}, {});
		const cachingPolicy = CachePolicyEntity.build({
			policy: CachePolicy.NetworkAndCache,
			maxAgeInSeconds: 60,
			isCacheable: true,
		});

		return this.get<IMarketPlaceMembers>(url, cachingPolicy);
	}

	public getMarketPlaceUserMembers(
		pageIndex: number,
		searchUser: string,
		filterFields: Array<DxFilterField>,
		sortFields: Array<DxSortField>,
		withDetails: WithDetails,
		useCache: boolean
	): Observable<{
		elements: ReadonlyArray<IMarketplaceUser>;
		total: number;
	}> {
		const endPoint = this.generateResourceUrl(
			'PROJECTS_IMPORT_DC_MARKETPLACE_USERS'
		);
		const filter = this.generateFilterFields(filterFields);
		const sortedFieldList = this.generateSortedFields(sortFields);
		const defaultQueryParams: IQueryParams = {
			page: pageIndex,
			size: DEFAULT_PAGE_SIZE,
			distinct: true,
			withDetails,
			...filter,
		};

		let queryParams: IQueryParams = searchUser
			? {
					...defaultQueryParams,
					['fullText.contains']: encodeURIComponent(searchUser),
			  }
			: defaultQueryParams;

		if (sortedFieldList.length > 0) {
			queryParams = {
				...queryParams,
				sort: sortedFieldList,
			};
		}

		const url = this.urlService.generateUrl(endPoint, {}, queryParams);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: true,
					policy: CachePolicy.NetworkAndCache,
			  })
			: CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
			  });

		return this.get<HttpResponse<ReadonlyArray<IMarketplaceUser>>>(
			url,
			cachePolicy,
			{
				observe: 'response',
			}
		).pipe(
			map((response) => ({
				elements: response.body ?? [],
				total: Number.parseInt(
					response.headers.get('x-total-count') ?? '0',
					10
				),
			}))
		);
	}

	public getMarketPlaceGroupMembers(
		pageIndex: number,
		searchGroup: string,
		filterFields: Array<DxFilterField>,
		sortFields: Array<DxSortField>,
		withDetails: WithDetails,
		useCache: boolean
	): Observable<{
		elements: ReadonlyArray<IMarketplaceGroup>;
		total: number;
	}> {
		const endPoint = this.generateResourceUrl(
			'PROJECTS_IMPORT_DC_MARKETPLACE_GROUPS'
		);
		const filter = this.generateFilterFields(filterFields);
		const sortedFieldList = this.generateSortedFields(sortFields);
		const defaultQueryParams: IQueryParams = {
			page: pageIndex,
			size: DEFAULT_PAGE_SIZE,
			distinct: true,
			withDetails,
			...filter,
		};

		let queryParams: IQueryParams = searchGroup
			? {
					...defaultQueryParams,
					['fullText.contains']: encodeURIComponent(searchGroup),
			  }
			: defaultQueryParams;

		if (sortedFieldList.length > 0) {
			queryParams = {
				...queryParams,
				sort: sortedFieldList,
			};
		}

		const url = this.urlService.generateUrl(endPoint, {}, queryParams);

		const cachePolicy = useCache
			? CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: true,
					policy: CachePolicy.NetworkAndCache,
			  })
			: CachePolicyEntity.build({
					maxAgeInSeconds: 60,
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
			  });

		return this.get<HttpResponse<ReadonlyArray<IMarketplaceGroup>>>(
			url,
			cachePolicy,
			{
				observe: 'response',
			}
		).pipe(
			map((response) => ({
				elements: response.body ?? [],
				total: Number.parseInt(
					response.headers.get('x-total-count') ?? '0',
					10
				),
			}))
		);
	}

	public getMembersMapping(
		consumersToMap: Map<string, ExpositionConsumerMappingEntity>
	): Observable<IMembersMapping> {
		const endpoint = this.generateResourceUrl('PROJECTS_IMPORT_AUTO_MAPPING');

		const body = this.generateConsumersToMapPayload(consumersToMap);

		const url = this.urlService.generateUrl(endpoint, {}, {});

		return this.http.post<IMembersMapping>(url, body);
	}

	private generateConsumersToMapPayload(
		consumersToMap: Map<string, ExpositionConsumerMappingEntity>
	): IConsumersToMap {
		const users: Array<IConsumerToMap> = [];
		const groups: Array<IConsumerToMap> = [];

		consumersToMap.forEach((entity) => {
			if (entity.isGroup) {
				groups.push({
					id: entity.id,
					name: entity.fullName,
				});
			} else {
				users.push({
					id: entity.id,
					name: entity.fullName,
				});
			}
		});

		return {
			users,
			groups,
		};
	}

	private generateFilterFields(
		filterList: Array<DxFilterField>
	): Record<string, string> {
		let resultFilter: Record<string, string> = {};
		filterList.map((filter) => {
			const { value, operator } = filter;

			if (operator === DxOperations.Between && Array.isArray(value)) {
				const resultDateFilter = this.handleDate(filter);
				resultFilter = {
					...resultFilter,
					...resultDateFilter,
				};
			} else if (value instanceof Date) {
				const resultDateFilter = this.handleDate(filter);
				resultFilter = {
					...resultFilter,
					...resultDateFilter,
				};
			} else if (typeof value === 'string') {
				const { key, value } = this.handleNonDateField(filter);
				resultFilter[key] = value;
			}
		});

		return resultFilter;
	}

	private generateSortedFields(
		sortedFieldList: Array<DxSortField>
	): Array<string> {
		return sortedFieldList.map((item) => {
			const { order } = item;
			return `${this.fieldsMapping[item.selector]},${order}`;
		});
	}

	private handleDate(filter: DxFilterField): Record<string, string> {
		const { field, operator, value } = filter;
		const mappedField = this.fieldsMapping[field];
		const mappingOperator = OperatorUtil.getOperator(operator as DxOperations);

		const _fieldValue = value as Date | Array<Date>;
		return DxGridUtil.generateDateFilter(
			mappedField,
			mappingOperator,
			_fieldValue
		);
	}

	private handleNonDateField(filter: DxFilterField): {
		key: string;
		value: string;
	} {
		const { field, operator, value } = filter;
		const _fieldValue = value as string;
		const mappedField = this.fieldsMapping[field];
		const mappingOperator = OperatorUtil.getOperator(operator as DxOperations);

		return {
			key: `${mappedField}.${mappingOperator}`,
			value: encodeURIComponent(_fieldValue),
		};
	}
}
